import type { SavePayoutPolicyDto } from "@fp/admin-api-client";
import {
  getListPayoutPoliciesQueryKey,
  useCreatePayoutPolicy,
  useListPayoutPolicies,
} from "@fp/admin-api-client/payout-policies/payout-policies.client";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import type { ApiErrorResponse } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { PayoutPolicyEditor } from "./-components/payout-policy-editor";
import { redirectUnlessPayoutPolicyEnabled, reportPayoutPolicyError } from "./-components/payout-policy-gate";
import { PayoutPolicyHelp } from "./-components/payout-policy-help";

export const Route = createFileRoute("/settings/payout-policies/new")({
  beforeLoad: ({ context: { queryClient } }) => redirectUnlessPayoutPolicyEnabled(queryClient),
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: policiesResponse } = useListPayoutPolicies();
  const maximumFeeRate = policiesResponse?.data[0]?.maximumFeeRate ?? 0;

  const createMutation = useCreatePayoutPolicy({
    mutation: {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: getListPayoutPoliciesQueryKey() });
        toast.success(`${response.data.name} was created`);
        navigate({ to: "/settings/payout-policies" });
      },
      onError: (err) => reportPayoutPolicyError(err as AxiosError<ApiErrorResponse>, "Failed to create the payout policy", queryClient, navigate),
    },
  });

  return (
    <AdminLayout>
      <AdminPageHeader
        title="New Payout Policy"
        parent={{ url: "/settings/payout-policies", label: "Payout Policies" }}
        titleActions={<PayoutPolicyHelp maximumFeeRate={maximumFeeRate} />}
      />
      <PayoutPolicyEditor
        maximumFeeRate={maximumFeeRate}
        isSaving={createMutation.isPending}
        saveLabel="Create Payout Policy"
        onSave={(body: SavePayoutPolicyDto) => createMutation.mutate({ data: body })}
      />
    </AdminLayout>
  );
}